/**
 * Language constants for the application.
 * This is the single source of truth for supported languages.
 */

export interface LanguageOption {
    code: string
    label: string
}

/**
 * Supported user interface languages.
 * Only English is currently supported for the UI.
 */
export const SUPPORTED_USER_LANGS: LanguageOption[] = [{ code: 'en', label: 'English' }]

/**
 * Supported target languages for learning.
 * French, German, and Spanish are currently supported.
 */
export const SUPPORTED_TARGET_LANGS: LanguageOption[] = [
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
    { code: 'es', label: 'Spanish' },
]

/**
 * Target language codes as a simple array for filtering/validation.
 */
export const TARGET_LANG_CODES = ['fr', 'de', 'es']

/**
 * User language codes as a simple array for filtering/validation.
 */
export const USER_LANG_CODES = ['en']

/**
 * All available languages (including user's native language options).
 */
export const ALL_LANGUAGES: LanguageOption[] = [...SUPPORTED_USER_LANGS, ...SUPPORTED_TARGET_LANGS]

/**
 * Get language label by code.
 */
export const getLanguageLabel = (code: string): string | null => {
    const allLanguages = [...SUPPORTED_USER_LANGS, ...SUPPORTED_TARGET_LANGS]
    const language = allLanguages.find((lang) => lang.code === code)
    return language?.label || null
}

/**
 * Check if a language code is supported as a target language.
 */
export const isSupportedTargetLanguage = (code: string): boolean => {
    return SUPPORTED_TARGET_LANGS.some((lang) => lang.code === code)
}

/**
 * Check if a language code is a supported user interface language.
 */
export const isSupportedUserLanguage = (code: string): boolean => {
    return SUPPORTED_USER_LANGS.some((lang) => lang.code === code)
}
